/**
 * WrongUnitExit — office only: "this photo is filed on the wrong unit, move it" (#6204, callable fieldService_galleryMove).
 *
 * Three pieces a viewer puts together for the photo on screen:
 *   - WrongUnitOffer: renders its children only when "Wrong unit" may be offered to this person for this photo;
 *   - WrongUnitButton: the viewer-toolbar button (an Offer around a button);
 *   - WrongUnitDialog: asks for the Generator ID of the unit the photo belongs to, then spells out the move naming
 *     BOTH units before anything is sent. The server resolves the ID and refuses an unknown, inactive or same unit.
 *
 * A viewer mounts these ONLY when its host opted in (`allowMove`: the office gallery and "All photos" viewers, never
 * the technician strip), so a host that did not opt in needs no signed-in user at all. The person must also look like
 * office staff (canOfferPhotoMove). Both are conveniences: the server is the gate.
 * Offline the move is disabled: it re-homes a row and rewrites the old unit's gallery, which cannot be queued.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightLeft } from 'lucide-react';
import { Button } from '../../forms/Button';
import { Input } from '../../forms/Input';
import { Card } from '../../shared/Card';
import { focusableWithin } from '../../shared/ResponsiveOverlay/useOverlayA11y';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsOffline } from '../../../hooks/useIsOffline';
import { useMoveGalleryPhoto } from '../../../hooks/fieldService/useMoveGalleryPhoto';
import { canMovePhoto, canOfferPhotoMove, checkMoveTarget } from '../../../lib/fieldService/photoMove';
import type { GeneratorPhoto } from '../../../types/fieldService/generatorPhotos';

interface WrongUnitDialogProps {
  photo: GeneratorPhoto;
  generatorDocId: string;
  onClose: () => void;
  /** The move landed: the photo no longer belongs to the unit on screen. Defaults to `onClose`. */
  onMoved?: () => void;
}

export function WrongUnitDialog({ photo, generatorDocId, onClose, onMoved }: WrongUnitDialogProps) {
  const [typed, setTyped] = useState('');
  const titleId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const move = useMoveGalleryPhoto(generatorDocId);
  const offline = useIsOffline();
  const from = photo.generatorId;
  const check = checkMoveTarget(typed, from);

  // While a move is in flight the dialog stays up (Cancel, Escape and the backdrop wait), so the person sees how it ended.
  const pending = move.isPending;
  const dismiss = () => {
    if (!pending) onClose();
  };

  // A real modal: focus moves in on open and goes back to whatever opened it on close (if that is still on the page:
  // after a move the opener left with its photo).
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    inputRef.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  // Escape closes ONLY this dialog (the viewer's own Escape yields to the last aria-modal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (!pending) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, pending]);

  // Tab stays inside the dialog. It must not reach the viewer behind: this dialog is a React child of the viewer's
  // toolbar, so an unhandled Tab bubbles to the viewer's own focus ring, which would pull focus out from under the overlay.
  const keepTabInside = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    e.stopPropagation();
    const box = dialogRef.current;
    if (!box) return;
    const focusables = focusableWithin(box);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !box.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !box.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const submit = () => {
    if (!check.ok || offline || pending) return;
    move.mutate({ photoId: photo.id, fromGeneratorId: from, toGeneratorId: check.toGeneratorId }, { onSuccess: onMoved ?? onClose });
  };

  return createPortal(
    <div data-testid="wrong-unit-backdrop" className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center" onClick={dismiss}>
      <div
        ref={dialogRef}
        className="w-full max-w-lg p-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={keepTabInside}
      >
        <Card>
          <h3 id={titleId} className="text-sm font-bold text-slate-900">Wrong unit</h3>
          <p className="mb-3 mt-1 text-sm text-slate-600">
            This photo is filed on <span className="font-semibold text-slate-900">{from}</span>. Which unit does it belong to?
          </p>
          <form
            aria-label="Move photo to another unit"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Input
              ref={inputRef}
              label="Generator ID of the right unit"
              aria-label="Generator ID of the right unit"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="G2506-6527"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={pending}
              error={typed.trim() !== '' && !check.ok ? check.reason : undefined}
            />
            {check.ok && (
              <p className="mt-3 text-sm text-slate-700" data-testid="wrong-unit-confirm">
                Move this photo from <span className="font-semibold">{from}</span> to{' '}
                <span className="font-semibold">{check.toGeneratorId}</span>? It leaves {from} and goes to &quot;All photos&quot; on{' '}
                {check.toGeneratorId}. If it was tied to a component on {from}, that tie is cleared.
              </p>
            )}
            {offline && <p className="mt-3 text-sm text-amber-700">You are offline. Moving a photo needs a connection.</p>}
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" size="lg" variant="ghost" onClick={dismiss} disabled={pending}>
                Cancel
              </Button>
              <Button type="submit" size="lg" variant="primary" disabled={!check.ok || offline} loading={pending} loadingText="Moving...">
                {check.ok ? `Move photo to ${check.toGeneratorId}` : 'Move photo'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>,
    document.body
  );
}

interface WrongUnitOfferProps {
  photo: GeneratorPhoto;
  children: ReactNode;
}

/** Children only when this person may be offered the move for this photo. Mount only under a host that opted in. */
export function WrongUnitOffer({ photo, children }: WrongUnitOfferProps) {
  const { user } = useAuth();
  return canOfferPhotoMove(user) && canMovePhoto(photo) ? <>{children}</> : null;
}

interface WrongUnitButtonProps {
  photo: GeneratorPhoto;
  onOpen: (photo: GeneratorPhoto) => void;
}

/** The viewer-toolbar button, in the viewer's own dark pill style (matches the plate pill beside it). */
export function WrongUnitButton({ photo, onOpen }: WrongUnitButtonProps) {
  return (
    <WrongUnitOffer photo={photo}>
      <button
        type="button"
        onClick={() => onOpen(photo)}
        title="This photo is on the wrong unit: move it"
        aria-haspopup="dialog"
        className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 text-sm text-white transition-colors hover:bg-white/20"
      >
        <ArrowRightLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Wrong unit</span>
      </button>
    </WrongUnitOffer>
  );
}
